import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as tagActions from '@redux/modules/tag'
import type { AppDispatch } from '@redux/store'

import defaultFormState, { requiredFields } from '@models/tag'
import type { TagModel, TagRequestOptions } from '@models/types'

export const generateUrls = () => ({
  hiddenTagsIndexUrl: '#/systemManager/tags?hidden=true',
  tagsIndexUrl: '#/systemManager/tags?hidden=false',
})

type CreateTagParams = {
  dispatch: AppDispatch,
  tagParams: Partial<TagModel>,
  requestOptions?: TagRequestOptions,
}

const createTag = (params: CreateTagParams) => {
  const { tagParams, dispatch, requestOptions } = params
  const { createTag: createFn } = tagActions

  return dispatch(createFn(tagParams, requestOptions))
}

type UpdateTagParams = {
  dispatch: AppDispatch,
  tag: TagModel,
  tagParams: Partial<TagModel>,
  requestOptions?: TagRequestOptions,
}

const updateTag = (params: UpdateTagParams) => {
  const {
    dispatch, tag, tagParams, requestOptions,
  } = params
  const { updateTag: updateFn } = tagActions

  const updatedParams = mergeAndStringifyEntityObjects(tag, tagParams, [])

  return dispatch(updateFn(updatedParams, requestOptions))
}

type CreateOrEditTagPayload = {
  tag: TagModel,
  showCreateOrEditTagModal: Function,
}

const createOrEditTag = (params: CreateOrEditTagPayload) => new Promise((resolve, reject) => {
  const { tag, showCreateOrEditTagModal } = params

  if (showCreateOrEditTagModal){
    const payload = {
      tag,
    }

    showCreateOrEditTagModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateOrEditTagModal not defined in PageContext callbacks'))
})

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useTagForm(
  tag: Partial<TagModel> = {},
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const tagForm = useForm(
    defaultFormState,
    { entity: tag, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [tag.id, tag.cache_key],
  )

  return {
    ...tagForm,
  }
}

export function useRelations(tag: Partial<TagModel>) {
  const { owner_id, owner_type } = tag || {}

  const brands = useSelector(reduxState => reduxState.entities.brands)

  const brand = owner_type === 'Brand' && owner_id ? brands[owner_id] : undefined

  return {
    brand,
  }
}

function useTag(initEntity: Partial<TagModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: tag }: { entity: TagModel } = useLatestEntity(initEntity, 'tags')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)
  const { showCreateOrEditTagModal } = callbacks || {}

  const { creating, loading, updating } = useSelector(reduxState => reduxState.tags)

  const { brand } = useRelations(tag)

  return {
    brand,
    callbacks: {
      createOrEditTag: () => createOrEditTag({ tag, showCreateOrEditTagModal }),
      createTag: (tagParams: Partial<TagModel>, requestOptions?: TagRequestOptions) => (
        createTag({ tagParams, dispatch, requestOptions })
      ),
      updateTag: (tagParams: Partial<TagModel>, requestOptions?: TagRequestOptions) => (
        updateTag({
          tag, tagParams, dispatch, requestOptions,
        })
      ),
    },
    creating,
    loading,
    tag,
    updating,
    urls: generateUrls(),
  }
}

export default useTag
